import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import BlogViewer from '../components/BlogViewer';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch post by slug
      const postsQuery = query(
        collection(db, 'blog-posts'),
        where('slug', '==', slug),
        where('status', '==', 'published')
      );
      const snapshot = await getDocs(postsQuery);

      if (snapshot.empty) {
        setError('Post not found');
        setLoading(false);
        return;
      }

      const postData = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };
      setPost(postData);

      // Fetch related posts
      const relatedQuery = query(
        collection(db, 'blog-posts'),
        where('status', '==', 'published')
      );
      const relatedSnapshot = await getDocs(relatedQuery);
      const related = relatedSnapshot.docs
        .filter(doc => doc.id !== postData.id)
        .slice(0, 3)
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      setRelatedPosts(related);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Error loading post');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="text-xl text-gray-600">Loading...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-xl text-gray-600 mb-8">
              The blog post you're looking for doesn't exist.
            </p>
            <Link
              to="/blog"
              className="inline-block bg-[#E85D44] text-white px-6 py-3 rounded-lg hover:bg-[#D44D34] transition-colors"
            >
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.metaTitle || post.title} | Merch Masters LK</title>
        <meta name="description" content={post.metaDescription || post.excerpt || ''} />
        {post.keywords && <meta name="keywords" content={post.keywords} />}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt || ''} />
        {post.featuredImage && <meta property="og:image" content={post.featuredImage} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle || post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt || ''} />
        {post.featuredImage && <meta name="twitter:image" content={post.featuredImage} />}
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-16">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Blog */}
          <Link
            to="/blog"
            className="inline-flex items-center text-[#E85D44] hover:text-[#D44D34] mb-8"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>

          {/* Featured Image */}
          {post.featuredImage && (
            <div className="aspect-video w-full rounded-lg overflow-hidden mb-8 shadow-lg">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Post Header */}
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>
            <div className="flex items-center text-gray-600">
              <span className="font-medium">{post.author?.name}</span>
              <span className="mx-3">•</span>
              <time>{formatDate(post.createdAt)}</time>
            </div>
          </header>

          {/* Post Content */}
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
            <BlogViewer content={post.content} />
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section className="mt-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Posts</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    {relatedPost.featuredImage && (
                      <div className="aspect-video w-full overflow-hidden bg-gray-200">
                        <img
                          src={relatedPost.featuredImage}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-gray-900 line-clamp-2 hover:text-[#E85D44] transition-colors">
                        {relatedPost.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
      <Footer />
    </>
  );
};

export default BlogPost;
